import { useState, useEffect } from "react";
import { Modal, Typography, Divider, Spin, Empty, Tag, Alert } from "antd";
import { SafetyOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const TermsOfServiceModal = ({ open, onClose }) => {
  const [termsData, setTermsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      loadTermsOfService();
    }
  }, [open]);

  const loadTermsOfService = () => {
    setLoading(true);
    setError(false);
    const timestamp = new Date().getTime();

    fetch(`/static/config/termsOfService.json?t=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setTermsData(data);
        setLoading(false);
        setError(false);
      })
      .catch(error => {
        console.warn('Could not load terms of service:', error.message);
        setTermsData(null);
        setError(true);
        setLoading(false);
      });
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <SafetyOutlined className="text-blue-600" />
          <span>Terms of Service</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            tip="Loading terms of service..."
          />
        </div>
      ) : error || !termsData ? (
        <div className="flex justify-center items-center py-12">
          <Empty
            description={
              <div>
                <Text type="secondary">Terms of service not available</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Please configure the terms of service in /public/static/config/termsOfService.json
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Title and Last Updated */}
          <div className="text-center">
            <Title level={3} className="text-blue-600 mb-2">
              {termsData.title}
            </Title>
            <Tag color="blue">Last Updated: {termsData.lastUpdated}</Tag>
          </div>

          {/* Important Notice */}
          <Alert
            message="Important Legal Notice"
            description="These terms are legally binding and must be read carefully. By using the EBMR system, you accept all terms and conditions outlined below."
            type="warning"
            icon={<WarningOutlined />}
            showIcon
          />

          <Divider />

          {/* Sections */}
          {termsData.sections?.map((section, index) => (
            <div key={index} className="mb-6">
              <Title level={5} className="text-blue-600 mb-3">
                {section.heading}
              </Title>

              {section.content && (
                <Paragraph className="text-gray-700 mb-3">
                  {section.content}
                </Paragraph>
              )}

              {section.list && section.list.length > 0 && (
                <ul className="list-disc pl-6 space-y-2">
                  {section.list.map((item, idx) => (
                    <li key={idx} className="text-gray-600">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Footer Note */}
          <Divider />
          <Alert
            message="Acknowledgment Required"
            description="By continuing to use this system, you certify that you have read, understood, and agree to comply with these Terms of Service and all applicable GMP regulations."
            type="info"
            showIcon
          />
        </div>
      )}
    </Modal>
  );
};

export default TermsOfServiceModal;
